import React from "react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";

import { AUTH_PROVIDER } from "../utils/devFlags";
import { CognitoAppAuthProvider, useCognitoFleetAuth } from "./cognitoAppAuth";
import { clearPageScopedFleetStateSession } from "./pageScopedSession";

/**
 * One session for the whole app, whichever identity provider issued it.
 *
 * The Auth0 retirement moves the console from Auth0 to the platform_auth
 * Cognito pool. Every consumer that used `useAuth0()` directly now reads this
 * instead, so the provider is a build-time configuration choice rather than a
 * code change:
 *
 *     REACT_APP_AUTH_PROVIDER=auth0     (default -- today's behaviour)
 *     REACT_APP_AUTH_PROVIDER=cognito
 *
 * That is what makes the cutover reversible in the way the migration plan
 * assumes. The backend already accepts BOTH issuers (SW-6982), so moving an
 * environment across, or moving it back, is one variable and a frontend
 * redeploy -- no backend change, no coordinated release, and beta and prod can
 * differ while beta is proving it.
 *
 * The shape below is deliberately the SMALLER of the two providers' surfaces.
 * Anything Auth0-specific that leaked into a component would re-couple the app
 * to Auth0 and defeat the point.
 */
export interface FleetAuthUser {
  email?: string;
  picture?: string;
}

/**
 * Which door the person chose on the sign-in page.
 *
 * The two populations have strictly separate ways in: staff federate through
 * Google Workspace and have no password; clients and partners have a native
 * email + password account and no Workspace identity. A wrong pick is not a
 * detour but an error -- pre_signup.py rejects a non-corporate Google account
 * outright -- so the console asks first and the provider uses the answer to
 * skip straight to the right screen where it can. A provider that cannot act
 * on it (Auth0's Universal Login presents its own chooser) ignores it.
 */
export type SignInPath = "employee" | "client";

/**
 * What the sign-in page learned before starting the redirect.
 *
 * `loginHint` is the address typed on the External door's email step. Cognito
 * prefills its password card from it, so the person types the address once,
 * here, instead of once here and again there. A provider that cannot use it
 * ignores it -- Cognito does not forward it to Google, and Auth0's Universal
 * Login is not offered it at all.
 */
export interface SignInOptions {
  loginHint?: string;
}

export interface FleetAuthState {
  /** True until the provider has settled. Never treat it as "signed out". */
  isLoading: boolean;
  isAuthenticated: boolean;
  /**
   * Why the session is not established, when the provider knows: a sign-in
   * callback that failed (invalid or stale `state`, an expired code,
   * `access_denied`), or a silent renew that did not. Set only alongside
   * `isAuthenticated: false` in any way a gate should act on. A gate that
   * ignores it and simply starts another login on "not authenticated" turns a
   * persistent failure into an invisible redirect loop (SW-6866).
   */
  error?: Error;
  user?: FleetAuthUser;
  /**
   * The ACCESS token, for use as a bearer.
   *
   * Access rather than ID on both providers, and not interchangeable: the fleet
   * API's gateway authorizer gates each route on a scope, and the Flask
   * validator pins `token_use: access` (SW-6982). An ID token carries the
   * client id as `aud`, no scope claim, and is rejected on every count.
   */
  getAccessToken: () => Promise<string>;
  /**
   * Start a sign-in. Never called automatically: the SignIn page waits for a
   * click, which is what keeps a failed callback from turning into a redirect
   * loop (SW-6866). `path` is the door the person chose; see SignInPath.
   */
  login: (path?: SignInPath, options?: SignInOptions) => Promise<void>;
  logout: () => void;
}

/** Auth0, via @auth0/auth0-react. */
function useAuth0FleetAuth(): FleetAuthState {
  const {
    isLoading,
    isAuthenticated,
    error,
    user,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0();

  return React.useMemo(
    () => ({
      isLoading,
      isAuthenticated,
      error,
      user: user ? { email: user.email, picture: user.picture } : undefined,
      getAccessToken: () => getAccessTokenSilently(),
      // The SignInPath hint is not used here: Universal Login shows its own
      // chooser, and the Auth0 build is on its way out (Auth0 retirement).
      login: () => loginWithRedirect(),
      logout: () => {
        clearPageScopedFleetStateSession();
        // Without `returnTo`, Auth0 sends the browser to the tenant's default
        // logout destination — which is prod, so logging out of beta landed on
        // fleet.aerovect.com. Each environment now logs out to itself; the
        // origins are already in the application's allowed logout URLs
        // (SW-6866).
        logout({ logoutParams: { returnTo: window.location.origin } });
      },
    }),
    [
      isLoading,
      isAuthenticated,
      error,
      user,
      getAccessTokenSilently,
      loginWithRedirect,
      logout,
    ],
  );
}

/**
 * The app's session provider. Wrap the tree in exactly one of these.
 *
 * Holds the Auth0 configuration that used to sit inline in `index.tsx`, so the
 * two providers are configured side by side and neither is the special case.
 */
export function FleetAuthProvider({
  children,
  auth0Domain,
  auth0ClientId,
  auth0Audience,
}: {
  children: React.ReactNode;
  auth0Domain: string;
  auth0ClientId: string;
  auth0Audience: string;
}) {
  if (AUTH_PROVIDER === "cognito") {
    return <CognitoAppAuthProvider>{children}</CognitoAppAuthProvider>;
  }

  return (
    <Auth0Provider
      domain={auth0Domain}
      clientId={auth0ClientId}
      // Persist the Auth0 session across full-page reloads. The Tractor State
      // page logs in to Cognito (Google SSO) via a full-page signinRedirect;
      // that reload wipes an in-memory session, and Chrome blocks Auth0's
      // silent-restore iframe (third-party cookies), so on return RequireAuth
      // would see no session and bounce the user back through an Auth0 login --
      // landing on the app root, never reaching /tractor-state. localStorage +
      // a refresh token survive the round-trip so the session restores without
      // depending on third-party cookies. (SW-6329)
      // Note: useRefreshTokens requires the Auth0 SPA app to have the refresh
      // token grant (with rotation) enabled, and the offline_access scope below.
      cacheLocation="localstorage"
      useRefreshTokens={true}
      // The Tractor State page runs its own Cognito (Google) login whose OIDC
      // callback returns to /tractor-state?code=&state=. Auth0 and Cognito use
      // the same code/state param names, so without this Auth0 would treat that
      // Cognito callback as its own and trigger a second, Auth0 login. Let
      // react-oidc-context own the /tractor-state callback (SW-6308). Auth0's
      // own redirect_uri is the origin root, so it never needs to handle a
      // callback under /tractor-state.
      skipRedirectCallback={window.location.pathname.startsWith(
        "/tractor-state",
      )}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: auth0Audience,
        // offline_access is required for useRefreshTokens to obtain a refresh
        // token; the rest are unchanged.
        scope:
          "openid profile email offline_access read:aerovect-vehicles read:delta-vehicles create:delta-ride-request create:aerovect-ride-request",
      }}
    >
      {children}
    </Auth0Provider>
  );
}

/*
 * Chosen once, at module load, from a build-time constant.
 *
 * Assigning the hook rather than branching inside one keeps this a single call
 * site, so it obeys the rules of hooks: the selection cannot change between
 * renders of a given bundle.
 */
const useFleetAuthImpl =
  AUTH_PROVIDER === "cognito" ? useCognitoFleetAuth : useAuth0FleetAuth;

export function useFleetAuth(): FleetAuthState {
  return useFleetAuthImpl();
}
